import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { calculateSurveyDates } from "@/helpers/survey-dates";
import { useSlug } from "@/hooks/useSlug";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { surveyMutations } from "@/queries/surveys/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { type CreateOrEditSurveyFormValues, SurveyForm } from "./SurveyForm";

export function CreateSurveyPage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSlug();
  const communityGroupQueries = useCommunityGroupQueries();
  const queryArgs = {
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;
  const { data: groupsData = [], isPending, error } = useQuery(communityGroupQueries.list(queryArgs));
  const addSurveyMutation = surveyMutations.useAddSurvey();

  const groups = useMemo(
    () => [
      { id: undefined, name: t("page.surveys.create-or-edit.form.group.general") },
      ...groupsData
        .filter((group) => group.canCreatePost || group.canCreateAnnouncement)
        .filter((group) => group.postingLevel !== "projectConnection")
        .map((group) => ({ id: group.id, name: group.name })),
    ],
    [t, groupsData],
  );
  const smartDates = useMemo(() => calculateSurveyDates(), []);
  const defaultFormValues: CreateOrEditSurveyFormValues = {
    type: "survey",
    category: "other",
    startDate: smartDates.startDate,
    endDate: smartDates.endDate,
    group: groups[0],
    title: "",
    description: "",
    thankYouMessage: "",
    questions: [{ type: "open", question: "", options: [] }],
    internal: false,
  };

  if (isPending) {
    return <FullSizeLoader withPadding />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <SurveyForm
      isEditMode={false}
      contentLanguageId={undefined}
      defaultFormValues={defaultFormValues}
      groups={groups}
      onSubmit={async (payload, category) => {
        const survey = await addSurveyMutation.mutateAsync({ ...payload, type: "survey", category });
        void navigate(routes.surveys.details({ slug, id: survey.id }));
      }}
      isSubmitting={addSurveyMutation.isPending}
      isPublished={false}
    />
  );
}
